import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCustomerProfile, useUpdateCustomerProfile } from '../api';

type AccountTab = 'profile' | 'addresses' | 'orders' | 'settings';

export function AccountPage() {
  const [activeTab, setActiveTab] = useState<AccountTab>('profile');
  const { data: profile, isLoading } = useCustomerProfile();
  const updateProfile = useUpdateCustomerProfile();

  // Form state for profile editing
  const [isEditing, setIsEditing] = useState(false);
  const [firstName, setFirstName] = useState(profile?.firstName ?? '');
  const [lastName, setLastName] = useState(profile?.lastName ?? '');
  const [phone, setPhone] = useState(profile?.phone ?? '');

  if (isLoading) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="container py-12">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4" />
            <div className="h-64 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  // Guest view - prompt to create account
  if (!profile) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="container py-12">
          <div className="max-w-md mx-auto bg-white rounded-xl shadow-sm border p-8 text-center">
            <UserCircleIcon className="w-20 h-20 mx-auto text-gray-300" />
            <h1 className="mt-4 text-2xl font-bold text-gray-900">
              Crea una cuenta
            </h1>
            <p className="mt-2 text-gray-600">
              Inicia sesion o crea una cuenta para ver tu historial de pedidos y administrar tu perfil.
            </p>
            <div className="mt-6 space-y-3">
              <button className="btn btn-primary w-full">
                Iniciar sesion
              </button>
              <button className="btn btn-secondary w-full">
                Crear cuenta
              </button>
            </div>
            <p className="mt-4 text-sm text-gray-500">
              ¿Compraste como invitado?{' '}
              <Link to="/pedidos/buscar" className="text-primary hover:text-primary-dark">
                Busca tu pedido
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleSaveProfile = async () => {
    await updateProfile.mutateAsync({
      firstName,
      lastName,
      phone,
    });
    setIsEditing(false);
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container py-8">
          <h1 className="text-2xl font-bold text-gray-900">Mi Cuenta</h1>
          <p className="mt-1 text-gray-600">
            Hola, {profile.firstName}
          </p>
        </div>
      </div>

      <div className="container py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar navigation */}
          <aside className="lg:w-64">
            <nav className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <TabButton
                active={activeTab === 'profile'}
                onClick={() => setActiveTab('profile')}
                icon={<UserIcon className="w-5 h-5" />}
              >
                Mi Perfil
              </TabButton>
              <TabButton
                active={activeTab === 'addresses'}
                onClick={() => setActiveTab('addresses')}
                icon={<LocationIcon className="w-5 h-5" />}
              >
                Direcciones
              </TabButton>
              <TabButton
                active={activeTab === 'orders'}
                onClick={() => setActiveTab('orders')}
                icon={<PackageIcon className="w-5 h-5" />}
              >
                Mis Pedidos
              </TabButton>
              <TabButton
                active={activeTab === 'settings'}
                onClick={() => setActiveTab('settings')}
                icon={<SettingsIcon className="w-5 h-5" />}
              >
                Configuracion
              </TabButton>
            </nav>
          </aside>

          {/* Main content */}
          <main className="flex-1">
            <div className="bg-white rounded-xl shadow-sm border p-6">
              {/* Profile tab */}
              {activeTab === 'profile' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900">
                      Informacion Personal
                    </h2>
                    {!isEditing && (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="text-primary hover:text-primary-dark text-sm font-medium"
                      >
                        Editar
                      </button>
                    )}
                  </div>

                  {isEditing ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Nombre
                          </label>
                          <input
                            type="text"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            className="input"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Apellido
                          </label>
                          <input
                            type="text"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            className="input"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Telefono
                        </label>
                        <input
                          type="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          className="input"
                        />
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={handleSaveProfile}
                          disabled={updateProfile.isPending}
                          className="btn btn-primary"
                        >
                          {updateProfile.isPending ? 'Guardando...' : 'Guardar'}
                        </button>
                        <button
                          onClick={() => setIsEditing(false)}
                          className="btn btn-secondary"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <InfoRow label="Nombre" value={`${profile.firstName} ${profile.lastName}`} />
                      <InfoRow label="Email" value={profile.email} />
                      <InfoRow label="Telefono" value={profile.phone ?? 'No registrado'} />
                    </div>
                  )}
                </div>
              )}

              {/* Addresses tab */}
              {activeTab === 'addresses' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900">
                      Mis Direcciones
                    </h2>
                    <button className="btn btn-primary text-sm">
                      + Agregar direccion
                    </button>
                  </div>

                  {profile.addresses && profile.addresses.length > 0 ? (
                    <div className="grid gap-4">
                      {profile.addresses.map((address) => (
                        <div
                          key={address.id}
                          className="p-4 border rounded-lg"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              {address.label && (
                                <span className="text-sm font-medium text-primary">
                                  {address.label}
                                </span>
                              )}
                              <p className="text-gray-900">{address.address}</p>
                              <p className="text-sm text-gray-600">
                                {address.district}, {address.city}
                              </p>
                            </div>
                            <button className="text-sm text-gray-500 hover:text-gray-700">
                              Editar
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">
                      No tienes direcciones guardadas
                    </p>
                  )}
                </div>
              )}

              {/* Orders tab */}
              {activeTab === 'orders' && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">
                    Mis Pedidos
                  </h2>
                  <div className="text-center py-12">
                    <PackageIcon className="w-16 h-16 mx-auto text-gray-300" />
                    <p className="mt-4 text-gray-500">
                      Aun no tienes pedidos
                    </p>
                    <Link to="/" className="mt-4 btn btn-primary inline-block">
                      Empezar a comprar
                    </Link>
                  </div>
                </div>
              )}

              {/* Settings tab */}
              {activeTab === 'settings' && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">
                    Configuracion
                  </h2>
                  <div className="space-y-6">
                    <div className="flex items-center justify-between py-4 border-b">
                      <div>
                        <p className="font-medium text-gray-900">
                          Notificaciones por email
                        </p>
                        <p className="text-sm text-gray-500">
                          Recibe ofertas y novedades
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" defaultChecked className="sr-only peer" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
                      </label>
                    </div>
                    <div className="flex items-center justify-between py-4 border-b">
                      <div>
                        <p className="font-medium text-gray-900">
                          Notificaciones SMS
                        </p>
                        <p className="text-sm text-gray-500">
                          Actualizaciones de pedidos
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
                      </label>
                    </div>
                    <button className="text-red-500 hover:text-red-600 text-sm font-medium">
                      Cerrar sesion
                    </button>
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 w-full px-4 py-3 text-left ${
        active
          ? 'bg-primary/10 text-primary border-l-4 border-primary'
          : 'text-gray-700 hover:bg-gray-50'
      }`}
    >
      {icon}
      <span className="font-medium">{children}</span>
    </button>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-2 border-b last:border-0">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-900">{value}</span>
    </div>
  );
}

function UserCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}

function LocationIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function PackageIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  );
}

function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}
